import { MenuContextProvider } from "@ubnt/store.core/lib/contexts/MenuContext";
import { MicrositeMenuContextProvider } from "@ubnt/store.core/lib/contexts/MicrositeMenuContext";
import { SiteId } from "@ubnt/store.core/lib/contexts/MenuContext/mapping";
import {
  Routes,
  Route,
  useLocation,
  Navigate,
  useParams,
} from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import { MicrositeProductPageContextProvider } from "@ubnt/store.core/lib/components/Microsites/context/MicrositeProductPageContext";
import { GlobalHeader } from "../Header";
import { useSiteSettings } from "../../context/SiteSettingsContext/context";
import { Footer } from "../Footer";
import {
  MainContainer,
  SectionContainer,
  MainContainerContent,
} from "./layout";
import "../../styles/fonts.scss";
import {
  DEFAULT_LANGUAGE,
  GLOBAL_SITE_CONFIG,
} from "context/SiteSettingsContext/constants";
import {
  getPageBasePaths,
  NOT_FOUND_PATH,
} from "context/SiteSettingsContext/utils";
import { getLanguageDirection } from "@ubnt/store.core/lib/contexts/MenuContext/utils";
import { VwoDebug } from "components/VwoDebug";
import { useBuyButton } from "hooks/useBuyButton";
import { useRemoveTrailingSlash } from "util/RemoveTrailingSlash";
import withTracker from "util/withTracker";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { StoreId } from "types";

// Dynamic redirect component
const DynamicRedirect = ({ to }: { to: string }) => {
  const { storeId = StoreId.Us, language = DEFAULT_LANGUAGE } = useParams();
  const basePath = `/${storeId}/${language}`;
  return <Navigate to={`${basePath}${to}`} replace />;
};

// External redirect component
const ExternalRedirect = ({ to }: { to: string }) => {
  useEffect(() => {
    window.location.href = to;
  }, [to]);
  return null;
};

// #############################################################################
// Pages
// #############################################################################
const LandingLazy = lazy(() =>
  import("pages/Sections/Landing").then((p) => ({
    default: p.Landing,
  })),
);

const Landing = withTracker(LandingLazy);
const NotFound = lazy(() => import("pages/NotFound/index"));
import SubRouteLayout from "pages/LayoutComponents/SubRouteLayout";
import { ROUTE_MAP, REDIRECT_MAP } from "./mapping";

gsap.registerPlugin(useGSAP);

// Pre-generate redirect routes at module level for performance
const REDIRECT_ROUTES = REDIRECT_MAP.flatMap(({ from, to }) => {
  const isExternal = to.startsWith("http");
  const RedirectComponent = isExternal ? ExternalRedirect : DynamicRedirect;

  return getPageBasePaths(from).map((path) => (
    <Route
      key={`${path}-${isExternal ? "external-" : ""}redirect`}
      path={path}
      element={<RedirectComponent to={to} />}
    />
  ));
});

// Root routes
const ROOT_ROUTES = [
  ...getPageBasePaths("/").map((path) => (
    <Route key={path} path={path} element={<Landing />} />
  )),
  <Route key={NOT_FOUND_PATH} path={NOT_FOUND_PATH} element={<NotFound />} />,
];

// Pre-generate routes from ROUTE_MAP
// All routes use SubRouteLayout which includes withTracker for analytics
const ROUTES_FROM_MAPPING = Object.values(ROUTE_MAP).flatMap((mapping) =>
  getPageBasePaths(mapping.path).map((path) => (
    <Route
      key={path}
      path={`${path}/*`}
      element={<SubRouteLayout mapping={mapping} />}
    />
  )),
);

const App = () => {
  const location = useLocation();
  const { store, language, isAppView } = useSiteSettings();
  const { showBuyButton } = useBuyButton();

  // #############################################################################
  // Used to remove focus from the active element (in navigation) when the location changes
  // #############################################################################
  useEffect(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [location]);

  // #############################################################################
  // Update `<html lang="...">`  on language change, right now it always EN on first render
  // #############################################################################
  useEffect(() => {
    document.documentElement.setAttribute("lang", language);
  }, [language]);

  // #############################################################################
  // Remove trailing slashes from URLs
  // #############################################################################
  useRemoveTrailingSlash();

  // #############################################################################
  // Render
  // #############################################################################
  return (
    <MenuContextProvider
      pathname={location.pathname}
      hash={location.hash}
      storeId={store.id}
      context={SiteId.Ui}
      globalSiteConfig={GLOBAL_SITE_CONFIG}
      language={language}
    >
      <MicrositeMenuContextProvider
        pathname={location.pathname}
        hash={location.hash}
        storeId={store.id}
        context={SiteId.Ui}
        globalSiteConfig={GLOBAL_SITE_CONFIG}
        language={language}
      >
        <MicrositeProductPageContextProvider showBuyButton={showBuyButton}>
          <VwoDebug />
          <MainContainer>
            <MainContainerContent>
              {/* Hide the global footer when microsite is embed in WebView for example, in UniFi Assistant mobile app */}
              {!isAppView && <GlobalHeader />}
              <SectionContainer dir={getLanguageDirection(language)}>
                <Suspense fallback={null}>
                  <Routes>
                    {/* Pre-generated root routes */}
                    {ROOT_ROUTES}

                    {/* Pre-generated routes from ROUTE_MAP */}
                    {ROUTES_FROM_MAPPING}

                    {/* Pre-generated redirect routes */}
                    {REDIRECT_ROUTES}

                    {/* 404 for anything unmatched */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </SectionContainer>
            </MainContainerContent>

            {/* Hide the footer when microsite is embed in WebView for example, in UniFi Assistant mobile app */}
            {!isAppView && <Footer />}
          </MainContainer>
        </MicrositeProductPageContextProvider>
      </MicrositeMenuContextProvider>
    </MenuContextProvider>
  );
};

export default App;
